import { BarChart3, Crosshair, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { formatElapsedTime } from '../lib/datalog';
import { datalogClient } from '../services/datalogClient';
import { enabledChannelIds, useWorkspaceStore } from '../store/workspace';
import type { ChannelDefinition, CursorResult, GraphPanel, RangeStatistics } from '../types';

type InspectorProps = {
  channels: ChannelDefinition[];
  onClose?: () => void;
};

function formatValue(value: number | null, unit?: string): string {
  if (value === null || !Number.isFinite(value)) return '—';
  const digits = Math.abs(value) >= 1000 ? 0 : Math.abs(value) >= 100 ? 1 : 2;
  return `${value.toFixed(digits)}${unit ? ` ${unit}` : ''}`;
}

/**
 * Keeps each statistic to three displayed digits so adjacent table columns
 * remain legible. Values are truncated rather than rounded so formatting can
 * never promote a value into a wider representation (for example, 99.99 to
 * 100).
 */
export function formatStatisticValue(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';

  const magnitude = Math.abs(value);
  const suffixes = ['', 'k', 'M', 'B', 'T'];
  const suffixIndex = Math.min(Math.floor(Math.log10(Math.max(magnitude, 1)) / 3), suffixes.length - 1);
  const scaledValue = value / (1000 ** suffixIndex);
  const scaledMagnitude = Math.abs(scaledValue);
  const decimalPlaces = scaledMagnitude >= 100 ? 0 : scaledMagnitude >= 10 ? 1 : 2;
  const factor = 10 ** decimalPlaces;
  const truncated = Math.trunc(scaledValue * factor) / factor;

  return `${truncated.toFixed(decimalPlaces)}${suffixes[suffixIndex]}`;
}

type GraphGroup = {
  graph: GraphPanel;
  channelIds: string[];
};

function uniqueGraphGroups(graphs: GraphPanel[]): GraphGroup[] {
  const seenChannelIds = new Set<string>();
  return [...graphs]
    .sort((left, right) => left.order - right.order)
    .flatMap((graph) => {
      const channelIds = graph.slots.flatMap((slot) => {
        if (!slot.channelId || seenChannelIds.has(slot.channelId)) return [];
        seenChannelIds.add(slot.channelId);
        return [slot.channelId];
      });
      return channelIds.length ? [{ graph, channelIds }] : [];
    });
}

function GraphDivider({ order }: { order: number }) {
  return <div className="graph-group-divider" aria-label={`Graph ${order + 1}`}><span>G{order + 1}</span></div>;
}

export function Inspector({ channels, onClose }: InspectorProps) {
  const graphs = useWorkspaceStore((state) => state.graphs);
  const cursorTime = useWorkspaceStore((state) => state.cursorTime);
  const visibleRange = useWorkspaceStore((state) => state.visibleTimeRange);
  const [cursor, setCursor] = useState<CursorResult | null>(null);
  const [statistics, setStatistics] = useState<RangeStatistics[]>([]);
  const channelIds = useMemo(() => enabledChannelIds(graphs), [graphs]);
  const graphGroups = useMemo(() => uniqueGraphGroups(graphs), [graphs]);
  const channelById = useMemo(() => new Map(channels.map((channel) => [channel.id, channel])), [channels]);
  const colorById = useMemo(() => {
    const result = new Map<string, string>();
    for (const graph of graphs) {
      for (const slot of graph.slots) if (slot.channelId && !result.has(slot.channelId)) result.set(slot.channelId, slot.color);
    }
    return result;
  }, [graphs]);

  useEffect(() => {
    if (cursorTime === null || !channelIds.length) {
      setCursor(null);
      return;
    }
    let active = true;
    void datalogClient.cursorValues(cursorTime, channelIds).then((result) => {
      if (active) setCursor(result);
    });
    return () => {
      active = false;
    };
  }, [channelIds.join('|'), cursorTime]);

  useEffect(() => {
    if (!channelIds.length) {
      setStatistics([]);
      return;
    }
    let active = true;
    void datalogClient.rangeStatistics(visibleRange, channelIds).then((result) => {
      if (active) setStatistics(result);
    });
    return () => {
      active = false;
    };
  }, [channelIds.join('|'), visibleRange.end, visibleRange.start]);

  return (
    <section className="inspector" aria-label="Cursor values and range statistics">
      <div className="panel-heading inspector-heading">
        <div>
          <p className="eyebrow">INSPECTOR</p>
          <span>Raw sample values</span>
        </div>
        {onClose && (
          <button className="icon-button drawer-close" onClick={onClose} aria-label="Close inspector">
            <X size={19} />
          </button>
        )}
      </div>

      <div className="inspector-cards">
      <section className="inspector-section inspector-card">
        <div className="inspector-card-header">
          <h2><Crosshair size={16} /> Cursor</h2>
          {cursor && (
            <div className="cursor-time">
              <strong>{formatElapsedTime(cursor.time)}</strong>
              <span>Sample {cursor.sampleIndex + 1}</span>
            </div>
          )}
        </div>
        {cursor ? (
          <div className="value-list">
            {graphGroups.map(({ graph, channelIds: groupChannelIds }) => (
              <div className="inspector-graph-group" key={graph.id}>
                <GraphDivider order={graph.order} />
                {groupChannelIds.map((id) => {
                  const channel = channelById.get(id);
                  if (!channel) return null;
                  return (
                    <div key={id} className="value-row">
                      <span className="value-color" style={{ background: colorById.get(id) }} />
                      <span>{channel.displayName}</span>
                      <strong>{formatValue(cursor.values[id], channel.unit)}</strong>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ) : (
          <p className="inspector-empty">Move over a graph or tap it to inspect an exact source sample.</p>
        )}
      </section>

      <section className="inspector-section inspector-card">
        <div className="inspector-card-header">
          <h2>
            <span className="inspector-section-title"><BarChart3 size={16} /> Range statistics</span>
          </h2>
        </div>
        <div className="range-summary">
          <span className="range-badge">Visible range</span>
          <span className="range-caption">
            {formatElapsedTime(visibleRange.start)} – {formatElapsedTime(visibleRange.end)}
          </span>
        </div>
        <p className="range-hint">Updates as you zoom or pan the graphs.</p>
        {channelIds.length ? (
          <table className="stats-table">
            <colgroup>
              <col className="stats-name-column" />
              <col span={3} className="stats-value-column" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col" aria-label="Channel" />
                <th scope="col">Min</th>
                <th scope="col">Avg</th>
                <th scope="col">Max</th>
              </tr>
            </thead>
            <tbody>
              {graphGroups.map(({ graph, channelIds: groupChannelIds }) => (
                <GraphStatsGroup
                  key={graph.id}
                  order={graph.order}
                  channelIds={groupChannelIds}
                  statistics={statistics}
                  channels={channelById}
                  colors={colorById}
                />
              ))}
            </tbody>
          </table>
        ) : (
          <p className="inspector-empty">Add a channel to a graph to see its statistics.</p>
        )}
      </section>
      </div>

    </section>
  );
}

type GraphStatsGroupProps = {
  order: number;
  channelIds: string[];
  statistics: RangeStatistics[];
  channels: Map<string, ChannelDefinition>;
  colors: Map<string, string>;
};

function GraphStatsGroup({ order, channelIds, statistics, channels, colors }: GraphStatsGroupProps) {
  const statisticsByChannelId = new Map(statistics.map((statistic) => [statistic.channelId, statistic]));
  return (
    <>
      <tr className="stats-group-divider-row"><td colSpan={4}><GraphDivider order={order} /></td></tr>
      {channelIds.map((channelId) => {
        const channel = channels.get(channelId);
        const statistic = statisticsByChannelId.get(channelId);
        if (!channel || !statistic) return null;
        return (
          <tr key={channelId} title={`${statistic.sampleCount.toLocaleString()} samples`}>
            <th scope="row">
              <span className="value-color" style={{ background: colors.get(channelId) }} />
              <span>{channel.displayName}</span>
            </th>
            <td>{formatStatisticValue(statistic.min)}</td>
            <td>{formatStatisticValue(statistic.average)}</td>
            <td>{formatStatisticValue(statistic.max)}</td>
          </tr>
        );
      })}
    </>
  );
}
